import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Stack,
  Typography,
  Skeleton,
  Alert,
  IconButton,
  LinearProgress,
} from "@mui/material";
import { useTheme, alpha } from "@mui/material/styles";
import {
  BarChart3,
  Search,
  TrendingUp,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  Link2,
  Sparkles,
  MousePointerClick,
  Eye,
  Percent,
  ListOrdered,
} from "lucide-react";

import { AlertNote } from "../../../components/AlertNote";
import { Button } from "../../../components/Button";
import { GoogleIcon } from "../../../components/icons/GoogleIcon";
import { Card, CardContent } from "../../../components/Card";
import { EmptyStateMessage } from "../../../components/EmptyStateMessage";
import { TrendDelta } from "../../../components/TrendDelta";
import { MetricCard } from "../../../components/MetricCard";
import { FilterSwitchGroup } from "../../../components/FilterSwitchGroup";
import { AnalyticsTable } from "../../../components/AnalyticsTable";
import { SortSelectorPopover } from "../../../components/SortSelectorPopover";
import { InfoTooltip } from "../../../components/InfoTooltip";
import { Progress } from "../../../components/Progress";
import { ConnectSearchConsoleModal } from "./ConnectSearchConsoleModal";
import { SeoConnectModal } from "../seoSetup/SeoConnectModal";
import { useSeoSetupStorageStatus } from "../seoSetup/hooks";
import {
  EVOLUTION_METRICS,
  EVOLUTION_METRIC_TABS,
  OUT_OF_RANGE_POSITION,
  VisibilityHistoryChart,
} from "./VisibilityHistoryChart";
import {
  MetricsDateRangeBar,
  defaultDateSelection,
  toIsoDate,
} from "../../../components/MetricsDateRangeBar";
import {
  getClientSearchConsoleLink,
  getSearchConsoleReport,
} from "../../../services/integrationsService";
import {
  getClientSeRankingLink,
  getSeRankingAiVisibility,
  getSeRankingBacklinks,
  getSeRankingKeywordHistory,
  getSeRankingOrganicOverview,
  getSeRankingRankings,
} from "../../../services/seRankingService";
import { useTheme as useCustomTheme } from "../../../theme/useTheme";
import { formatDecimal, formatNumber, formatPercent } from "../../../utils/numberFormat";
import { SEARCH_QUERY_COLUMNS } from "./searchQueries";
import { useAuth } from "../../../components/contexts/AuthContext";
import { SEO_PERMISSION_MESSAGE, getSeoProvider } from "@/utils/agencyFeatures";
import { CLIENT_ERROR_MESSAGE, googleReauthMessage, isGoogleReauthError } from "./googleReauth";

const RANKINGS_PAGE_SIZE = 10;
// Sorted by the backend, since the table is paginated there.
const RANKINGS_SORT_FIELDS = [
  { value: "movement", label: "Mayor movimiento" },
  { value: "change", label: "Variación" },
  { value: "position", label: "Posición" },
  { value: "volume", label: "Volumen" },
  { value: "keyword", label: "Keyword" },
];
const RANKINGS_SORT_DIRECTIONS = [
  { value: "asc", label: "Ascendente" },
  { value: "desc", label: "Descendente" },
];

/**
 * SE Ranking is allowed to answer with nulls (a keyword that ranks nowhere, a
 * Bing metric it does not have), and a null is not a zero: an unknown value is
 * rendered as an em dash so it is never read as "no clicks" or "position 0".
 */
const orDash = (value, formatter) =>
  value === null || value === undefined ? "—" : formatter(value);

// The `engine` value for every engine at once, like SE Ranking's own
// "Todos los motores de búsqueda" view.
const ALL_ENGINES = "all";

/**
 * Short toggle label for an engine: "Google", "Google Móvil", "Bing".
 *
 * The full SE Ranking name ("Google Mobile Spain") only comes back when two
 * engines would otherwise share a label, e.g. a project tracking two regions.
 */
const ENGINE_TYPE_LABELS = {
  google: "Google",
  google_mobile: "Google Móvil",
  bing: "Bing",
  yahoo: "Yahoo",
  yandex: "Yandex",
};

const engineLabels = (engines = []) => {
  const short = engines.map(
    (engine) => ENGINE_TYPE_LABELS[engine.type] || engine.name || engine.id
  );
  return engines.reduce(
    (labels, engine, index) => {
      const isDuplicate = short.filter((label) => label === short[index]).length > 1;
      labels[engine.id] = isDuplicate ? engine.name || short[index] : short[index];
      return labels;
    },
    { [ALL_ENGINES]: "Todos los motores" }
  );
};

/** Engine tabs of a card: "Todos" first, then each engine the project tracks. */
const engineTabs = (engines = [], labels = {}) => [
  { value: ALL_ENGINES, label: "Todos" },
  ...engines.map((engine) => ({ value: engine.id, label: labels[engine.id] })),
];

/** The engine the backend defaults to when none is sent: Google, else the first. */
const defaultEngineId = (engines = []) =>
  (engines.find((engine) => engine.type === "google") || engines[0])?.id ?? null;

// A keyword repeats once per engine under "Todos", so its id alone is not a row id.
const rankingRowId = (row) => `${row.engine_id}:${row.keyword_id}`;

const RANKING_BUCKETS = [
  { key: "top3", label: "Top 3", opacity: 1 },
  { key: "top10", label: "Top 10", opacity: 0.75 },
  { key: "top20", label: "Top 20", opacity: 0.5 },
  { key: "top100", label: "Top 100", opacity: 0.3 },
];

const RANKINGS_TABLE_MIN_WIDTH = 640;

// Search Console headline cards, laid out like the "Tráfico y usuarios" KPIs.
// `delta` says how each figure moves: percent, rate points, or plain position
// places (the backend already signs those so that climbing is positive).
const SC_KPI_CARDS = [
  {
    key: "clicks",
    label: "Clics",
    icon: MousePointerClick,
    tone: "neutral",
    format: formatNumber,
    delta: "percent",
  },
  {
    key: "impressions",
    label: "Impresiones",
    icon: Eye,
    tone: "primary",
    format: formatNumber,
    delta: "percent",
  },
  {
    key: "ctr",
    label: "CTR",
    icon: Percent,
    tone: "neutral",
    format: (value) => formatPercent(value * 100),
    delta: "points",
  },
  {
    key: "position",
    label: "Posición media",
    icon: ListOrdered,
    tone: "primary",
    format: (value) => formatDecimal(value),
    delta: "places",
  },
];

const KPI_GRID_SX = {
  display: "grid",
  gridTemplateColumns: {
    xs: "minmax(0, 1fr)",
    sm: "repeat(2, minmax(0, 1fr))",
    lg: "repeat(4, minmax(0, 1fr))",
  },
  gap: 2,
};

// Responsive padding shared by every card in the panel.
const CARD_PADDING = { xs: 2, sm: 3 };

// Tab strips never wrap; they scroll sideways on narrow screens.
const NO_WRAP_SX = { flexWrap: "nowrap" };

const userFacingError = (err) => {
  if (isGoogleReauthError(err)) {
    return googleReauthMessage(err, "pulsa «Configurar conexión» y vuelve a autorizarla");
  }
  const status = err?.response?.status;
  if (status === 502) {
    return "Google no ha respondido. Vuelve a intentarlo en unos minutos.";
  }
  if (status === 403 || status === 404) {
    return "No tienes acceso a las métricas de este cliente.";
  }
  return "No se han podido cargar los datos de visibilidad.";
};

const avgPositionInfo = (seo) =>
  `Media calculada por ${seo.source} sobre todas las keywords monitorizadas, incluidas las que no posicionan, que cuentan como posición ~101. Por eso puede ser peor que la posición de las keywords que sí aparecen.`;

/**
 * The ranges the keyword detail chart offers, mirroring SE Ranking's own.
 * Two years is the cap the backend enforces.
 */
const KEYWORD_HISTORY_RANGES = [
  { value: 7, label: "7 días" },
  { value: 30, label: "1 mes" },
  { value: 90, label: "3 meses" },
  { value: 180, label: "6 meses" },
  { value: 365, label: "1 año" },
  { value: 730, label: "2 años" },
];

const DEFAULT_KEYWORD_HISTORY_RANGE = 90;

/**
 * The panel that opens under a ranking row: that keyword's position history.
 *
 * It owns its own range and its own fetch, so collapsing the row throws the
 * request away and opening another keyword never reuses a stale series. The
 * backend caps the range at two years and answers 404 when the keyword is not
 * tracked on the engine the table reports.
 */
function KeywordHistoryDetail({ linkId, keywordId, engineId, keyword, readOnly = false }) {
  const { user } = useAuth();
  const seo = getSeoProvider(user);
  const theme = useTheme();
  const [days, setDays] = useState(DEFAULT_KEYWORD_HISTORY_RANGE);
  const [history, setHistory] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!linkId || !keywordId) return undefined;

    let cancelled = false;
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - days);

    const load = async () => {
      setIsLoading(true);
      setError("");
      try {
        const data = await getSeRankingKeywordHistory(linkId, {
          keywordId,
          engineId,
          startDate: toIsoDate(start),
          endDate: toIsoDate(end),
        });
        if (!cancelled) setHistory(data);
      } catch (err) {
        if (cancelled) return;
        setHistory(null);
        if (readOnly) {
          setError(err?.response?.status === 404 ? "No hay histórico disponible para esta keyword." : CLIENT_ERROR_MESSAGE);
        } else {
          setError(
            err?.response?.status === 404
              ? `${seo.Source} no hace seguimiento de esta keyword en este motor.`
              : "No se ha podido cargar el histórico de esta keyword."
          );
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [linkId, keywordId, engineId, days, readOnly]);

  const series = history?.series || [];
  const hasPositions = series.some(
    (point) => point.value !== null && point.value !== undefined
  );
  // `position` keeps SE Ranking's answer for the tooltip; `value` is the
  // plotted one, so a day with no ranking dives instead of breaking the line.
  const chartSeries = series.map((point) => ({
    date: point.date,
    position: point.value,
    value: point.value ?? OUT_OF_RANGE_POSITION,
  }));

  return (
    <Box
      sx={{
        paddingX: { xs: 1.5, sm: 2 },
        paddingTop: 1.5,
        paddingBottom: 1,
        borderRadius: 2,
        backgroundColor: alpha(theme.palette.primary.main, 0.03),
      }}
    >
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", sm: "center" }}
        spacing={1}
        sx={{ marginBottom: 1 }}
      >
        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
          {keyword}
        </Typography>
        <Box sx={{ overflowX: "auto", maxWidth: "100%", scrollbarWidth: "none", "&::-webkit-scrollbar": { display: "none" } }}>
          <FilterSwitchGroup
            options={KEYWORD_HISTORY_RANGES}
            selectedValue={days}
            onChange={setDays}
            disabled={isLoading}
            containerSx={NO_WRAP_SX}
            optionsSx={NO_WRAP_SX}
          />
        </Box>
      </Stack>

      {/* Best / worst / current, the three figures SE Ranking puts next to
          this chart. They ignore the days the keyword did not rank. */}
      {history && (
        <Stack direction="row" spacing={3} sx={{ marginBottom: 1 }} flexWrap="wrap" useFlexGap>
          {[
            ["Actual", history.current_position],
            ["Mejor", history.best_position],
            ["Peor", history.worst_position],
          ].map(([label, value]) => (
            <Stack key={label} direction="row" spacing={0.75} alignItems="baseline">
              <Typography variant="caption" color="text.secondary">
                {label}
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 700 }}>
                {orDash(value, formatNumber)}
              </Typography>
            </Stack>
          ))}
        </Stack>
      )}

      {isLoading && <Skeleton variant="rounded" height={220} />}

      {!isLoading && error && (
        <Typography variant="body2" color="text.secondary" sx={{ paddingY: 3, textAlign: "center" }}>
          {error}
        </Typography>
      )}

      {!isLoading && !error && !hasPositions && (
        <Typography variant="body2" color="text.secondary" sx={{ paddingY: 3, textAlign: "center" }}>
          Esta keyword no ha posicionado en el top 100 durante el periodo seleccionado.
        </Typography>
      )}

      {!isLoading && !error && hasPositions && (
        <>
          <VisibilityHistoryChart history={chartSeries} metric="keywordPosition" />
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block", marginTop: 0.5 }}
          >
            {`Los valles al fondo del gráfico son días en los que ${seo.source} no situó la keyword en el top 100.`}
          </Typography>
        </>
      )}
    </Box>
  );
}

/** Search Console headline figure on the app's standard MetricCard. */
function KpiCard({ card, kpi, isLoading, showDelta, showPrevious }) {
  const { colors, neutrals } = useCustomTheme();
  const isPrimary = card.tone === "primary";
  const iconProps = {
    icon: card.icon,
    iconColor: isPrimary ? colors.primary.main : neutrals.gray[600],
    iconBackgroundColor: isPrimary ? `${colors.primary.main}15` : neutrals.gray[100],
  };

  if (isLoading) {
    return <MetricCard {...iconProps} label={card.label} value="" isLoading />;
  }
  if (!kpi) return null;

  const delta =
    card.delta === "percent" ? (
      <TrendDelta value={kpi.change_percent} suffix="%" showEmpty />
    ) : card.delta === "points" ? (
      <TrendDelta value={kpi.change_points} suffix=" pts" showEmpty />
    ) : (
      <TrendDelta value={kpi.change_points} maximumFractionDigits={1} showEmpty />
    );

  return (
    <MetricCard
      {...iconProps}
      label={card.label}
      value={card.format(kpi.value)}
      valueAdornment={showDelta ? delta : null}
      footer={
        showPrevious ? (
          <Typography variant="caption" color="text.disabled">
            {`Antes: ${card.format(kpi.previous)}`}
          </Typography>
        ) : null
      }
      sx={{ height: "100%", minWidth: 0 }}
    />
  );
}

/**
 * `readOnly` is the client's own metrics page: no connect/configure actions and
 * no toolbar card; the page owns the date bar and passes it down as `dates`.
 * There, `showSearchConsole` / `showSeRanking` say which integrations are
 * linked; the missing one renders nothing at all. Elsewhere the panel draws its
 * own bar, controlled through `dates` + `onDatesChange` when given.
 */
export function VisibilityPanel({
  clientId,
  readOnly = false,
  showSearchConsole = true,
  showSeRanking = true,
  dates: datesProp,
  onDatesChange,
}) {
  const theme = useTheme();
  const { colors } = useCustomTheme();
  const { user } = useAuth();
  const seo = getSeoProvider(user);
  const canProvisionSeo = !readOnly;
  const { status: seoSetupStatus } = useSeoSetupStorageStatus(clientId);

  const [scLink, setScLink] = useState(null);
  const [scReport, setScReport] = useState(null);

  const [isLoadingLinks, setIsLoadingLinks] = useState(true);
  const [isLoadingReports, setIsLoadingReports] = useState(false);
  const [error, setError] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);

  // SE Ranking. Everything hangs off `srLink`: the metric endpoints are detail
  // actions on the client↔site link, so its id has to be resolved before any
  // of them can be called, and its absence is the empty state.
  const [srLink, setSrLink] = useState(null);
  const [srOverview, setSrOverview] = useState(null);
  // Site engine id (or ALL_ENGINES) picked in each card's engine tabs; null
  // lets the backend default to Google. The two cards are independent.
  const [srEngineId, setSrEngineId] = useState(null);
  const [srRankingsEngineId, setSrRankingsEngineId] = useState(null);
  // Which series the "Evolución" chart plots: "visibility" or "position".
  const [srEvolutionMetric, setSrEvolutionMetric] = useState("visibility");
  const [srBacklinks, setSrBacklinks] = useState(null);
  const [srAi, setSrAi] = useState(null);
  // One flag per call, so each card renders as soon as its own data lands
  // instead of the whole panel waiting for the slowest SE Ranking route.
  const [isLoadingSrLink, setIsLoadingSrLink] = useState(true);
  const [isLoadingOverview, setIsLoadingOverview] = useState(false);
  const [isLoadingBacklinks, setIsLoadingBacklinks] = useState(false);
  const [isLoadingAi, setIsLoadingAi] = useState(false);
  const [srRankings, setSrRankings] = useState({
    results: [],
    count: 0,
    detail: "",
  });
  const [srRankingsPage, setSrRankingsPage] = useState(1);
  const [srRankingsSort, setSrRankingsSort] = useState({ field: "movement", direction: "desc" });
  // Only one ranking row is open at a time, the way SE Ranking's own table
  // behaves; the detail panel fetches its own series.
  const [expandedKeywordId, setExpandedKeywordId] = useState(null);
  const [isLoadingRankings, setIsLoadingRankings] = useState(false);
  const [srError, setSrError] = useState("");
  const [isSeRankingModalOpen, setIsSeRankingModalOpen] = useState(false);
  // Bumped after connecting or disconnecting to re-run every SE Ranking call.
  const [srReloadKey, setSrReloadKey] = useState(0);

  // Same date bar as the "Tráfico y usuarios" tab, so the two are read the
  // same way; SE Ranking's summary reads the plain range, GA4/Search Console
  // read it exactly like the traffic tab does.
  const [localDates, setDates] = useState(defaultDateSelection);
  const dates = datesProp || localDates;
  const handleDatesChange = onDatesChange || setDates;
  const startDate = toIsoDate(dates.range?.from);
  const endDate = toIsoDate(dates.range?.to);
  const compareStartDate = toIsoDate(dates.compareRange?.from);
  const compareEndDate = toIsoDate(dates.compareRange?.to);

  useEffect(() => {
    let cancelled = false;

    const loadLinks = async () => {
      if (!showSearchConsole) {
        setScLink(null);
        setIsLoadingLinks(false);
        return;
      }
      setIsLoadingLinks(true);
      try {
        const searchConsole = await getClientSearchConsoleLink(clientId);
        if (cancelled) return;
        setScLink(searchConsole);
      } catch (err) {
        if (!cancelled) setError(userFacingError(err));
      } finally {
        if (!cancelled) setIsLoadingLinks(false);
      }
    };

    loadLinks();
    return () => {
      cancelled = true;
    };
  }, [clientId, showSearchConsole]);

  useEffect(() => {
    if (isLoadingLinks) return undefined;
    if (!scLink?.id) return undefined;
    if (!startDate || !endDate) return undefined;

    let cancelled = false;

    const loadReport = async () => {
      setIsLoadingReports(true);
      setError("");
      try {
        const searchConsole = await getSearchConsoleReport(scLink.id, {
          startDate,
          endDate,
          compareStartDate,
          compareEndDate,
        });
        if (cancelled) return;
        setScReport(searchConsole);
      } catch (err) {
        if (cancelled) return;
        setError(userFacingError(err));
      } finally {
        if (!cancelled) setIsLoadingReports(false);
      }
    };

    loadReport();
    return () => {
      cancelled = true;
    };
  }, [isLoadingLinks, scLink?.id, startDate, endDate, compareStartDate, compareEndDate]);

  /**
   * The client↔site link, which makes every metric endpoint addressable.
   *
   * Its absence is the empty state rather than an error. The metric calls
   * below each hang off it independently, so a slow route (AIRT fans out one
   * request per AI engine) never holds back the others.
   */
  useEffect(() => {
    let cancelled = false;

    const loadLink = async () => {
      if (!showSeRanking) {
        setSrLink(null);
        setIsLoadingSrLink(false);
        return;
      }
      setIsLoadingSrLink(true);
      setSrError("");
      try {
        const link = await getClientSeRankingLink(clientId);
        if (cancelled) return;
        setSrLink(link);
        if (!link?.id) {
          setSrOverview(null);
          setSrBacklinks(null);
          setSrAi(null);
          setSrRankings({ results: [], count: 0, detail: "" });
        }
      } catch {
        if (cancelled) return;
        setSrLink(null);
        setSrError(`No se han podido cargar los datos de ${seo.label}.`);
      } finally {
        if (!cancelled) setIsLoadingSrLink(false);
      }
    };

    loadLink();
    return () => {
      cancelled = true;
    };
  }, [clientId, srReloadKey, showSeRanking]);

  // The only SE Ranking call that depends on the date bar and the engine.
  useEffect(() => {
    if (!srLink?.id) return undefined;

    let cancelled = false;
    const linkId = srLink.id;

    const loadOverview = async () => {
      setIsLoadingOverview(true);
      setSrError("");
      try {
        const overview = await getSeRankingOrganicOverview(linkId, {
          startDate,
          endDate,
          engineId: srEngineId,
        });
        if (!cancelled) setSrOverview(overview);
      } catch (err) {
        if (cancelled) return;
        setSrOverview(null);
        setSrError(
          err?.response?.status === 502
            ? `${seo.Source} no ha respondido. Vuelve a intentarlo en unos minutos.`
            : `No se han podido cargar los datos de ${seo.label}.`
        );
      } finally {
        if (!cancelled) setIsLoadingOverview(false);
      }
    };

    loadOverview();
    return () => {
      cancelled = true;
    };
  }, [srLink?.id, startDate, endDate, srEngineId, srReloadKey]);

  // Backlinks and AI visibility ignore the date range, so they are not
  // refetched when it changes, and each degrades to null on its own.
  useEffect(() => {
    if (!srLink?.id) return undefined;

    let cancelled = false;
    const linkId = srLink.id;

    setIsLoadingBacklinks(true);
    getSeRankingBacklinks(linkId)
      .catch(() => null)
      .then((backlinks) => {
        if (cancelled) return;
        setSrBacklinks(backlinks);
        setIsLoadingBacklinks(false);
      });

    setIsLoadingAi(true);
    getSeRankingAiVisibility(linkId)
      .catch(() => null)
      .then((ai) => {
        if (cancelled) return;
        setSrAi(ai);
        setIsLoadingAi(false);
      });

    return () => {
      cancelled = true;
    };
  }, [srLink?.id, srReloadKey]);

  // A new range, engine or order starts again from the first page.
  useEffect(() => {
    setSrRankingsPage(1);
  }, [startDate, endDate, srRankingsEngineId, srRankingsSort]);

  // Rankings are paginated by the backend, so every page change is a new call.
  useEffect(() => {
    if (!srLink?.id) return undefined;

    let cancelled = false;
    const linkId = srLink.id;

    const loadRankings = async () => {
      setIsLoadingRankings(true);
      try {
        const data = await getSeRankingRankings(linkId, {
          page: srRankingsPage,
          pageSize: RANKINGS_PAGE_SIZE,
          startDate,
          endDate,
          engineId: srRankingsEngineId,
          ordering: `${srRankingsSort.direction === "desc" ? "-" : ""}${srRankingsSort.field}`,
        });
        if (!cancelled) {
          setSrRankings(data);
          // The open row belongs to the page that was on screen.
          setExpandedKeywordId(null);
        }
      } catch {
        if (!cancelled) setSrRankings({ results: [], count: 0, detail: "" });
      } finally {
        if (!cancelled) setIsLoadingRankings(false);
      }
    };

    loadRankings();
    return () => {
      cancelled = true;
    };
  }, [srLink?.id, srRankingsPage, startDate, endDate, srRankingsEngineId, srRankingsSort, srReloadKey]);

  const handleSeRankingConnected = (link) => {
    setSrLink(link);
    setSrEngineId(null);
    setSrRankingsEngineId(null);
    setSrRankingsPage(1);
    setSrError("");
    setSrReloadKey((key) => key + 1);
  };

  const handleSeRankingDisconnected = () => {
    setSrLink(null);
    setSrOverview(null);
    setSrEngineId(null);
    setSrRankingsEngineId(null);
    setSrBacklinks(null);
    setSrAi(null);
    setSrRankings({ results: [], count: 0, detail: "" });
    setSrRankingsPage(1);
    setExpandedKeywordId(null);
    setSrError("");
  };

  const handleConnected = (newLink) => {
    setScLink(newLink);
  };

  const handleDisconnected = () => {
    setScLink(null);
    setScReport(null);
    setError("");
  };

  const srLinked = Boolean(srLink?.id);
  const srVisibilityChange = srOverview?.visibility_change ?? null;
  const srEngineLabels = useMemo(
    () => engineLabels(srOverview?.engines),
    [srOverview?.engines]
  );
  const srEngineLabel = srOverview?.engine
    ? srEngineLabels[srOverview.engine.id]
    : "";
  // Before the first overview lands, and while the link resolves, a card has
  // nothing to show but a skeleton.
  const isLoadingSrOverview =
    isLoadingSrLink || (srLinked && !srOverview && (isLoadingOverview || !srError));

  /**
   * Search Console headline KPIs, in the same cards and grid as the traffic
   * tab. Not linked yet, the row collapses into the connect card instead.
   */
  const searchConsoleKpis = () => {
    if (!showSearchConsole) return null;
    if (!isLoadingLinks && !scLink) {
      if (readOnly) return null;
      return (
        <Card>
          <CardContent sx={{ padding: CARD_PADDING }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              Google Search
            </Typography>
            <EmptyStateMessage
              icon={Search}
              message="Este cliente todavía no tiene un recurso de Google Search Console vinculado."
              sx={{ py: 4 }}
            />
            <Stack alignItems="center">
              <Button variant="outline" onClick={() => setIsModalOpen(true)}>
                <GoogleIcon size={16} style={{ marginRight: 8 }} />
                Conectar Google Search Console
              </Button>
            </Stack>
          </CardContent>
        </Card>
      );
    }

    const isLoading = isLoadingLinks || isLoadingReports;
    // A failed report leaves nothing to show; the error alert above explains it.
    if (!isLoading && !scReport?.kpis) return null;

    return (
      <Box sx={KPI_GRID_SX}>
        {SC_KPI_CARDS.map((card) => (
          <KpiCard
            key={card.key}
            card={card}
            kpi={scReport?.kpis?.[card.key]}
            isLoading={isLoading}
            showDelta={dates.compareEnabled}
            showPrevious={dates.compareEnabled && dates.showPreviousValues}
          />
        ))}
      </Box>
    );
  };

  /** Top 10 Search Console queries; the position delta only shows while comparing, like the KPIs. */
  const searchQueriesCard = () => {
    if (!showSearchConsole || !scLink) return null;
    const isLoading = isLoadingLinks || isLoadingReports;
    if (!isLoading && !scReport?.queries) return null;

    const columns = SEARCH_QUERY_COLUMNS.map((column) => {
      if (column.key === "query") return { ...column, width: "minmax(180px, 2.4fr)" };
      if (column.key === "position" && !dates.compareEnabled) {
        return { ...column, render: (row) => formatDecimal(row.position) };
      }
      return column;
    });

    return (
      <AnalyticsTable
        title="Búsquedas que traen clics"
        subtitle="Las 10 búsquedas con más clics del periodo · Google Search Console"
        columns={columns}
        rows={scReport?.queries || []}
        getRowKey={(row) => row.query}
        isLoading={isLoading}
        skeletonRows={10}
        minTableWidth={560}
        emptyState={
          <EmptyStateMessage
            icon={Search}
            message="Search Console no ha devuelto búsquedas con clics en este periodo."
            sx={{ py: 4 }}
          />
        }
      />
    );
  };

  /**
   * Shared "this client has no SE Ranking project" card.
   *
   * Mirrors what searchConsoleKpis does when `!scLink`: the same empty state
   * plus the button that opens the dialog that fixes it.
   */
  const seoEmptyMessage = canProvisionSeo
    ? "Este cliente todavía no tiene el seguimiento SEO configurado."
    : seo.branded
      ? "Este cliente todavía no tiene un proyecto de SE Ranking vinculado."
      : `Este cliente todavía no tiene ${seo.label} vinculados. ${SEO_PERMISSION_MESSAGE}`;

  const seRankingEmptyCard = (title, icon, message) => (
    <Card sx={{ height: "100%" }}>
      <CardContent sx={{ padding: CARD_PADDING }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          {title}
        </Typography>
        <EmptyStateMessage icon={icon} message={message} sx={{ py: 4 }} />
        <Stack alignItems="center">
          <Button variant="outline" onClick={() => setIsSeRankingModalOpen(true)}>
            <Link2 size={16} style={{ marginRight: 8 }} />
            {canProvisionSeo && seoSetupStatus === "pending" ? "Continuar configuración" : `Conectar ${seo.label}`}
          </Button>
        </Stack>
      </CardContent>
    </Card>
  );

  const seRankingLoadingCard = (chartHeight = 240) => (
    <Card sx={{ height: "100%" }}>
      <CardContent sx={{ padding: CARD_PADDING }}>
        <Skeleton variant="text" width={220} />
        <Skeleton variant="text" width={180} />
        <Skeleton variant="rounded" height={chartHeight} sx={{ marginTop: 2 }} />
      </CardContent>
    </Card>
  );

  /**
   * Toolbar card at the top of the tab: the date range (it drives both SE
   * Ranking's overview and the Search Console card) and the connection
   * buttons. Only shown once one of the two is linked; connecting Search
   * Console for the first time stays in the "Google Search" card's empty state.
   */
  const toolbarCard = () => {
    if (readOnly || (!scLink && !srLinked)) return null;

    return (
      <Card>
        <CardContent sx={{ padding: CARD_PADDING }}>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            justifyContent="space-between"
            alignItems={{ xs: "stretch", sm: "flex-end" }}
            flexWrap="wrap"
            useFlexGap
            spacing={2}
          >
            <Box sx={{ minWidth: 0, maxWidth: "100%" }}>
              <MetricsDateRangeBar
                value={dates}
                onChange={handleDatesChange}
                disabled={isLoadingReports || isLoadingOverview}
              />
            </Box>

            <Stack
              direction={{ xs: "column", sm: "row" }}
              flexWrap="wrap"
              useFlexGap
              spacing={{ xs: 1.5, sm: 2 }}
            >
              {scLink && (
                <Button variant="outline" onClick={() => setIsModalOpen(true)}>
                  <GoogleIcon size={16} style={{ marginRight: 8 }} />
                  Configurar Search Console
                </Button>
              )}
              {srLinked && (
                <Button
                  variant="outline"
                  onClick={() => setIsSeRankingModalOpen(true)}
                >
                  <Link2 size={16} style={{ marginRight: 8 }} />
                  Configurar {seo.label}
                </Button>
              )}
            </Stack>
          </Stack>
        </CardContent>
      </Card>
    );
  };

  const visibilityCard = () => {
    if (!showSeRanking) return null;
    if (isLoadingSrOverview) return seRankingLoadingCard(220);

    if (!srLinked) {
      if (readOnly) return null;
      return seRankingEmptyCard(
        "Visibilidad orgánica",
        BarChart3,
        seoEmptyMessage
      );
    }

    if (!srOverview?.engine) {
      return (
        <Card>
          <CardContent sx={{ padding: CARD_PADDING }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              Visibilidad orgánica
            </Typography>
            <EmptyStateMessage
              icon={BarChart3}
              message={
                srOverview
                  ? `Este proyecto de ${seo.label} no hace seguimiento de ningún buscador.`
                  : "No se han podido cargar los datos de visibilidad."
              }
              sx={{ py: 4 }}
            />
          </CardContent>
        </Card>
      );
    }

    const distribution = srOverview.distribution || {};
    const tracked = srOverview.keywords_tracked || 0;
    // Refetching for another engine or range keeps the previous figures on
    // screen, dimmed, instead of collapsing the card into a skeleton.
    const isRefreshing = isLoadingOverview;
    const evolution =
      EVOLUTION_METRICS[srEvolutionMetric] || EVOLUTION_METRICS.visibility;

    const headlineCell = (label, content, { divider = true } = {}) => (
      <Box
        sx={{
          minWidth: 0,
          paddingRight: { md: 3 },
          borderRight: { md: divider ? `1px solid ${theme.palette.divider}` : "none" },
        }}
      >
        <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
          {label}
        </Typography>
        {content}
      </Box>
    );

    const bigNumberSx = {
      fontWeight: 700,
      fontSize: { xs: "22px", sm: "28px" },
      lineHeight: 1.2,
    };
    const sectionLabelSx = {
      fontWeight: 700,
      color: "text.secondary",
      letterSpacing: 0.5,
      display: "block",
      marginBottom: 1,
    };

    return (
      <Card>
        <CardContent sx={{ padding: CARD_PADDING }}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="flex-start"
          flexWrap="wrap"
          useFlexGap
          spacing={1}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              Visibilidad orgánica
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {`% de visibilidad de ${seo.label} sobre ${formatNumber(tracked)} keywords monitorizadas · ${srEngineLabel}`}
            </Typography>
          </Box>

        {srOverview.engines?.length > 1 && (
          <Box
            sx={{
              overflowX: "auto",
              maxWidth: "100%",
              scrollbarWidth: "none",
              "&::-webkit-scrollbar": { display: "none" },
            }}
          >
            <FilterSwitchGroup
              options={engineTabs(srOverview.engines, srEngineLabels)}
              selectedValue={srOverview.engine.id}
              onChange={(engineId) => {
                if (engineId) setSrEngineId(engineId);
              }}
              disabled={isRefreshing}
              containerSx={NO_WRAP_SX}
              optionsSx={NO_WRAP_SX}
            />
          </Box>
        )}
        </Stack>

        <Box sx={{ opacity: isRefreshing ? 0.5 : 1, transition: "opacity 150ms" }}>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "repeat(2, minmax(0, 1fr))",
                md: "repeat(4, auto)",
              },
              justifyContent: { md: "start" },
              gap: { xs: 2, sm: 3 },
              marginTop: 2.5,
            }}
          >
            {headlineCell(
              "Visibilidad (%)",
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                {/* visibility_percent from SE Ranking: already a percentage, and
                    its change is in percentage points, hence " pts". */}
                <Typography sx={bigNumberSx}>
                  {orDash(srOverview.visibility_percent, formatPercent)}
                </Typography>
                <TrendDelta
                  value={srVisibilityChange}
                  suffix=" pts"
                  minimumFractionDigits={2}
                  maximumFractionDigits={2}
                />
              </Stack>
            )}
            {headlineCell(
              "Keywords monitorizadas",
              <Typography sx={bigNumberSx}>{formatNumber(tracked)}</Typography>
            )}
            {headlineCell(
              "Posición media del seguimiento",
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                {/* SE Ranking reports the average as a whole number; shown as is. */}
                <Typography sx={bigNumberSx}>
                  {orDash(srOverview.avg_position, formatNumber)}
                </Typography>
                {/* Already signed so that positive means the average climbed
                    (a lower position), which TrendDelta renders green. */}
                <TrendDelta
                  value={srOverview.avg_position_change}
                  maximumFractionDigits={1}
                />
                <Typography variant="caption" color="text.secondary">
                  {seo.label}
                </Typography>
                <InfoTooltip size={14} title={avgPositionInfo(seo)} />
              </Stack>
            )}
            {headlineCell(
              "",
              <Stack direction="row" spacing={3}>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Mejoran
                  </Typography>
                  <Typography sx={{ ...bigNumberSx, color: theme.palette.success.main }}>
                    {formatNumber(srOverview.keywords_improved)}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Bajan
                  </Typography>
                  <Typography sx={{ ...bigNumberSx, color: theme.palette.error.main }}>
                    {formatNumber(srOverview.keywords_declined)}
                  </Typography>
                </Box>
              </Stack>,
              { divider: false }
            )}
          </Box>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "minmax(0, 1fr)", md: "minmax(0, 1.5fr) minmax(0, 1fr)" },
              gap: 4,
              marginTop: 3,
            }}
          >
            <Box sx={{ minWidth: 0 }}>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                flexWrap="wrap"
                useFlexGap
                spacing={1}
                sx={{ marginBottom: 1 }}
              >
                <Typography variant="caption" sx={{ ...sectionLabelSx, marginBottom: 0 }}>
                  EVOLUCIÓN · RANGO SELECCIONADO
                </Typography>
                <Box
                  sx={{
                    overflowX: "auto",
                    maxWidth: "100%",
                    scrollbarWidth: "none",
                    "&::-webkit-scrollbar": { display: "none" },
                  }}
                >
                  <FilterSwitchGroup
                    options={EVOLUTION_METRIC_TABS.map((value) => ({
                      value,
                      label: EVOLUTION_METRICS[value].label,
                    }))}
                    selectedValue={srEvolutionMetric}
                    onChange={(metric) => {
                      if (metric) setSrEvolutionMetric(metric);
                    }}
                    containerSx={NO_WRAP_SX}
                    optionsSx={NO_WRAP_SX}
                  />
                </Box>
              </Stack>
              {srOverview[evolution.historyKey]?.length ? (
                <VisibilityHistoryChart
                  history={srOverview[evolution.historyKey]}
                  metric={srEvolutionMetric}
                />
              ) : (
                <EmptyStateMessage
                  icon={BarChart3}
                  message={evolution.emptyMessage(seo.Source)}
                  sx={{ py: 4 }}
                />
              )}

            </Box>

            <Box sx={{ minWidth: 0 }}>
              <Typography variant="caption" sx={sectionLabelSx}>
                DISTRIBUCIÓN DE RANKINGS
              </Typography>
              <Stack spacing={2.5} sx={{ marginTop: 2 }}>
                {RANKING_BUCKETS.map((bucket) => {
                  const count = distribution[bucket.key] ?? 0;
                  return (
                    <Stack key={bucket.key} direction="row" spacing={2} alignItems="center">
                      <Typography variant="body2" sx={{ fontWeight: 600, width: 64, flexShrink: 0 }}>
                        {bucket.label}
                      </Typography>
                      <LinearProgress
                        variant="determinate"
                        value={tracked ? Math.min((count / tracked) * 100, 100) : 0}
                        sx={{
                          flex: 1,
                          height: 10,
                          borderRadius: 5,
                          backgroundColor: alpha(theme.palette.primary.main, 0.08),
                          "& .MuiLinearProgress-bar": {
                            backgroundColor: alpha(theme.palette.primary.main, bucket.opacity),
                            borderRadius: 5,
                          },
                        }}
                      />
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 700, width: 40, textAlign: "right", flexShrink: 0 }}
                      >
                        {formatNumber(count)}
                      </Typography>
                    </Stack>
                  );
                })}
              </Stack>

              {/* Backlinks used to be a KPI card; they now close the
                  distribution column so the headline row keeps its design. */}
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="baseline"
                spacing={2}
                sx={{
                  marginTop: 3,
                  paddingTop: 2,
                  borderTop: `1px solid ${theme.palette.divider}`,
                }}
              >
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  Backlinks activos
                </Typography>
                {isLoadingBacklinks ? (
                  <Skeleton variant="text" width={90} />
                ) : (
                  <Stack direction="row" spacing={1} alignItems="baseline">
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                      {srBacklinks ? orDash(srBacklinks.total, formatNumber) : "—"}
                    </Typography>
                    {srBacklinks && (
                      <Typography variant="caption" color="text.secondary">
                        {`${orDash(srBacklinks.domains, formatNumber)} dominios`}
                      </Typography>
                    )}
                  </Stack>
                )}
              </Stack>
            </Box>
          </Box>
        </Box>
        </CardContent>
      </Card>
    );
  };

  /** One engine row of the "Visibilidad en IA" card: name, mention stats, bar. */
  function AiEngineRow({ engine, isLast }) {
    // AI Overview is the one engine SE Ranking reports a link position for, so
    // its second line reads "con enlace directo" instead of "citada en N
    // prompts", the way the mockup distinguishes the two.
    const isLinkEngine = /overview/i.test(engine.name);
    const secondaryLine = isLinkEngine
      ? `${formatNumber(engine.mentions_total)} menciones · ${formatNumber(engine.direct_link_count)} con enlace directo`
      : `${formatNumber(engine.mentions_total)} menciones · citada en ${formatNumber(engine.prompts_with_mention)} prompts`;

    return (
      <Box
        sx={{
          paddingY: 1.5,
          borderBottom: isLast ? "none" : `1px solid ${theme.palette.divider}`,
        }}
      >
        <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {engine.name}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {secondaryLine}
            </Typography>
          </Box>
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ flexShrink: 0, width: { xs: 112, sm: 140 } }}>
            <Progress soft height={6} value={Math.min(engine.mention_percent_in_top ?? 0, 100)} sx={{ flex: 1 }} />
            <Typography variant="body2" sx={{ fontWeight: 700, width: 40, textAlign: "right" }}>
              {orDash(engine.mention_percent_in_top, (v) => `${Math.round(v)}%`)}
            </Typography>
          </Stack>
        </Stack>
      </Box>
    );
  }

  const isLoadingAiCard = isLoadingSrLink || isLoadingAi;
  const hasAiData = Boolean(srLinked && srAi?.tracked && srAi.engines?.length);
  const showAiCard = isLoadingAiCard || hasAiData;

  const aiVisibilityCard = () => {
    if (isLoadingAiCard) {
      return (
        <Card sx={{ height: "100%" }}>
          <CardContent sx={{ padding: CARD_PADDING }}>
            <Skeleton variant="text" width={160} />
            <Stack spacing={1.5} sx={{ marginTop: 2 }}>
              {[0, 1].map((index) => (
                <Skeleton key={index} variant="rounded" height={56} />
              ))}
            </Stack>
          </CardContent>
        </Card>
      );
    }

    // No AI data (no linked project, AIRT not set up, or no engines): hide the card.
    if (!hasAiData) return null;

    return (
      <Card sx={{ height: "100%" }}>
        <CardContent sx={{ padding: CARD_PADDING }}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            flexWrap="wrap"
            useFlexGap
            spacing={1}
          >
            <Stack direction="row" spacing={1} alignItems="center">
              <Sparkles size={16} color={theme.palette.primary.main} />
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                Visibilidad en IA
              </Typography>
            </Stack>
            {/* Overall presence, formerly the "RANKING IA" KPI card. */}
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography sx={{ fontWeight: 700, fontSize: "1.125rem" }}>
                {formatPercent(srAi.overall_presence_percent ?? 0, 1)}
              </Typography>
              <TrendDelta
                value={srAi.overall_presence_change}
                suffix=" pts"
                minimumFractionDigits={1}
                maximumFractionDigits={1}
              />
            </Stack>
          </Stack>
          <Typography variant="caption" color="text.secondary">
            {`Presencia en respuestas de IA · ${srAi.brand}`}
          </Typography>

          <Stack sx={{ marginTop: 1 }}>
            {srAi.engines.map((engine, index) => (
              <AiEngineRow
                key={engine.llm_id}
                engine={engine}
                isLast={index === srAi.engines.length - 1 && !srAi.top_prompt}
              />
            ))}
          </Stack>

          {srAi.top_prompt && (
            <Box
              sx={{
                marginTop: 2,
                padding: 2,
                borderRadius: "12px",
                backgroundColor: alpha(theme.palette.primary.main, 0.06),
              }}
            >
              <Typography
                variant="caption"
                sx={{ fontWeight: 700, color: theme.palette.primary.main, letterSpacing: 0.5 }}
              >
                PROMPT CON MÁS TRACCIÓN
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 700, marginTop: 0.5 }}>
                {`«${srAi.top_prompt.text}»`}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {`Citada en ${srAi.top_prompt.engines_cited} de ${srAi.top_prompt.engines_total} motores.`}
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>
    );
  };

  const rankingsCard = () => {
    if (!showSeRanking) return null;
    if (isLoadingSrLink) return seRankingLoadingCard();

    if (!srLinked) {
      if (readOnly) return null;
      return seRankingEmptyCard(
        "Principales movimientos de rankings",
        TrendingUp,
        seoEmptyMessage
      );
    }

    const rows = srRankings.results || [];
    const rangeStart = rows.length ? (srRankingsPage - 1) * RANKINGS_PAGE_SIZE + 1 : 0;
    const rangeEnd = (srRankingsPage - 1) * RANKINGS_PAGE_SIZE + rows.length;
    const hasNextPage = rangeEnd < (srRankings.count ?? 0);
    const rankingsEngines = srOverview?.engines || [];
    const isAllEngines = srRankingsEngineId === ALL_ENGINES;

    const columns = [
      {
        key: "keyword",
        header: "Keyword",
        width: "minmax(180px, 2fr)",
        cellSx: { color: "text.primary", gap: 0.75 },
        render: (row) => (
          <>
            {/* The caret is the affordance for the row opening its history
                chart, and it rotates with the row's state. */}
            <ChevronDown
              size={14}
              style={{
                flexShrink: 0,
                transition: "transform 160ms ease",
                transform:
                  expandedKeywordId === rankingRowId(row) ? "rotate(0deg)" : "rotate(-90deg)",
              }}
            />
            <Typography variant="body2" sx={{ fontWeight: 500, minWidth: 0 }} noWrap title={row.keyword}>
              {row.keyword}
            </Typography>
          </>
        ),
      },
      ...(isAllEngines
        ? [
            {
              key: "engine",
              header: "Motor",
              width: "minmax(96px, 0.8fr)",
              render: (row) => srEngineLabels[row.engine_id] || "—",
            },
          ]
        : []),
      {
        // A null position means "does not rank", never 0.
        key: "position",
        header: "Posición",
        align: "right",
        width: "minmax(80px, 0.8fr)",
        highlighted: true,
        render: (row) => orDash(row.position, formatNumber),
      },
      {
        // Start of the range against its end, signed so that positive means
        // "climbed", even though positions run the other way.
        key: "change",
        header: "Variación",
        align: "right",
        width: "minmax(88px, 0.8fr)",
        render: (row) => (
          <TrendDelta value={row.change} maximumFractionDigits={0} showEmpty />
        ),
      },
      {
        key: "volume",
        header: "Volumen",
        align: "right",
        width: "minmax(80px, 0.8fr)",
        render: (row) => orDash(row.volume, formatNumber),
      },
      {
        key: "url",
        header: "URL",
        width: "minmax(160px, 1.6fr)",
        render: (row) =>
          row.url ? (
            <Typography
              component="a"
              href={row.url}
              target="_blank"
              rel="noopener noreferrer"
              variant="body2"
              noWrap
              title={row.url}
              // The click belongs to the link, not to the row underneath it.
              onClick={(event) => event.stopPropagation()}
              sx={{
                minWidth: 0,
                color: colors.link || theme.palette.primary.main,
                textDecoration: "underline",
                "&:hover": { textDecoration: "underline", opacity: 0.85 },
              }}
            >
              {row.url}
            </Typography>
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ minWidth: 0 }}>
              —
            </Typography>
          ),
      },
    ];

    return (
      <AnalyticsTable
        title="Principales movimientos de rankings"
        subtitle="Haz clic en una keyword para ver su evolución de posiciones"
        actions={
          <SortSelectorPopover
            sortField={srRankingsSort.field}
            sortDirection={srRankingsSort.direction}
            onSortFieldChange={(field) => setSrRankingsSort((sort) => ({ ...sort, field }))}
            onSortDirectionChange={(direction) => setSrRankingsSort((sort) => ({ ...sort, direction }))}
            fieldOptions={RANKINGS_SORT_FIELDS}
            directionOptions={RANKINGS_SORT_DIRECTIONS}
          />
        }
        tabs={
          rankingsEngines.length > 1
            ? engineTabs(rankingsEngines, srEngineLabels)
            : undefined
        }
        tabsInHeader
        activeTab={srRankingsEngineId ?? defaultEngineId(rankingsEngines)}
        onTabChange={(engineId) => {
          if (engineId) setSrRankingsEngineId(engineId);
        }}
        tabsDisabled={isLoadingRankings}
        columns={columns}
        rows={rows}
        getRowKey={(row, index) =>
          row.keyword_id ? rankingRowId(row) : `${row.keyword}-${index}`
        }
        onRowClick={(row) =>
          setExpandedKeywordId((current) =>
            current === rankingRowId(row) ? null : rankingRowId(row)
          )
        }
        isRowExpanded={(row) => expandedKeywordId === rankingRowId(row)}
        renderRowDetail={(row) => (
          <KeywordHistoryDetail
            linkId={srLink.id}
            keywordId={row.keyword_id}
            engineId={row.engine_id}
            keyword={row.keyword}
            readOnly={readOnly}
          />
        )}
        isLoading={isLoadingRankings}
        skeletonRows={rows.length || 6}
        minTableWidth={RANKINGS_TABLE_MIN_WIDTH}
        emptyState={
          // `detail` is how the backend says the project tracks engines, but
          // not Google: a state worth naming, not "no data yet".
          <EmptyStateMessage
            icon={TrendingUp}
            message={
              srRankings.detail
                ? `Este proyecto de ${seo.label} no hace seguimiento del motor de Google, así que no hay posiciones que mostrar.`
                : "Todavía no hay keywords con posiciones en este proyecto."
            }
            sx={{ py: 4 }}
          />
        }
        footer={
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            spacing={1}
          >
            <Typography variant="caption" color="text.secondary">
              {isLoadingRankings
                ? "Cargando keywords…"
                : `Mostrando ${rangeStart}-${rangeEnd} de ${formatNumber(srRankings.count)} keywords.`}
            </Typography>
            <Stack direction="row" spacing={0.5} alignItems="center">
              <IconButton
                size="small"
                aria-label="Página anterior"
                disabled={srRankingsPage === 1 || isLoadingRankings}
                onClick={() => setSrRankingsPage((page) => page - 1)}
              >
                <ChevronLeft size={16} />
              </IconButton>
              <IconButton
                size="small"
                aria-label="Página siguiente"
                disabled={!hasNextPage || isLoadingRankings}
                onClick={() => setSrRankingsPage((page) => page + 1)}
              >
                <ChevronRight size={16} />
              </IconButton>
            </Stack>
          </Stack>
        }
      />
    );
  };

  return (
    <Stack spacing={2}>
      {/* The client only gets a generic message: no provider names, accounts or setup steps. */}
      {readOnly ? (
        (error || srError) && <Alert severity="error">{CLIENT_ERROR_MESSAGE}</Alert>
      ) : (
        <>
          {error && <Alert severity="error">{error}</Alert>}
          {srError && <AlertNote variant="warning">{srError}</AlertNote>}
        </>
      )}
      {!readOnly && srLink?.domain_mismatch && (
        <AlertNote variant="warning">
          {`El proyecto de ${seo.label} vinculado (${srLink.domain_mismatch.property_host}) no coincide con la web del cliente (${srLink.domain_mismatch.client_host}).`}
        </AlertNote>
      )}

      {toolbarCard()}

      {searchConsoleKpis()}

      {searchQueriesCard()}

      {/* Full width: the headline figures, chart and distribution sit side by
          side, as in the client's mockup. */}
      {visibilityCard()}

      {showAiCard && aiVisibilityCard()}

      {rankingsCard()}

      {!readOnly && (
        <>
          <ConnectSearchConsoleModal
            open={isModalOpen}
            onOpenChange={setIsModalOpen}
            clientId={clientId}
            currentLink={scLink}
            onConnected={handleConnected}
            onDisconnected={handleDisconnected}
          />

          <SeoConnectModal
            open={isSeRankingModalOpen}
            onOpenChange={setIsSeRankingModalOpen}
            clientId={clientId}
            currentLink={srLink}
            onConnected={handleSeRankingConnected}
            onDisconnected={handleSeRankingDisconnected}
          />
        </>
      )}
    </Stack>
  );
}

export default VisibilityPanel;
